/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {RadioGroup} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import Accordion from '@ion/dls/src/components/accordion/ExpansionPanel';
import Chip from '@ion/dls/src/components/chip/Chip';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import {BatteryLowIcon} from '@ion/dls/src/icons/BatteryLowIcon';
import {HarshEventIcon} from '@ion/dls/src/icons/HarshEventIcon';
import {IgnitionOffIcon} from '@ion/dls/src/icons/IgnitionOffIcon';
import {IgnitionOnIcon} from '@ion/dls/src/icons/IgnitionOnIcon';
import {InputEventIcon} from '@ion/dls/src/icons/InputEventIcon';
import {StopIcon} from '@ion/dls/src/icons/StopIcon';
import {
  getInputEventColorBar,
  getInputMixedSelection
} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  HistoryInputEvents,
  HistoryInputEventsEnum,
  HistoryReportTypeEnum
} from '@ion/dls/src/types/types';
import {
  LayerSettingsHeader,
  LayerSettingsProps,
  LayerSettingsStyles
} from './LayerSettings';
import styles from './LayerChooser.module.scss';

// defines the styles used by the TripHistoryLayerSettings
const useStyles = makeStyles(theme => (LayerSettingsStyles()));

// Min 24 Hours
const MIN_HOURS = 1;
// Max 24 Hours
const MAX_HOURS = 24;

// Icon size
const LEGEND_ICON_SIZE = 15;

// Separator to use when selecting two inputs
export const HISTORY_SEPARATOR = '&&';

// Defines the unique identifier for the History Layer
export const HISTORY_LAYER = 'history';

// Max Input Event Selected
export const MAX_INPUT_SELECTED = 3;

/**
 * @CurrentLayerSettings
 *
 * The Trip History Layer Settings provides a component which allows users to select
 * specific Trip History layer related attributes to be displayed within a DLSMap.
 *
 * The TripHistoryLayerSettings component represents the "History Layers" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1904%3A13962
 *
 * ```typescript
 *
 *   <TripHistoryLayerSettings onToggle={}
 *     isEnabled={}
 *     map={} />
 *
 * ```
 */
export const TripHistoryLayerSettings = ({
  map,
  layers,
  isEnabled,
  onToggle,
  expandedLayer,
  onToggleExpand,
  loadAssetHistory,
  setHistoryInterval,
  historyReportType,
  setHistoryReportType,
  setHistoryInputEvents,
  assetsHistoryLoading,
  historyIntervalInHours,
  historyInputEvents
}: LayerSettingsProps) => {
  const classes: any                = useStyles();
  const [defaultLayer, setSelected] = useState(isEnabled);
  const [error, setError]           = useState(false);
  const [interval, setInterval]     = useState(historyIntervalInHours);
  const label                       = translate('maps:history.header');
  const detailLabel                 = translate('maps:history.fromPast');

  const enabled             = isEnabled !== defaultLayer ? isEnabled : defaultLayer;
  const isByAssetActive     = historyReportType === HistoryReportTypeEnum.Asset;
  const inputEventsSelected = historyInputEvents.filter(({isSelected}) => isSelected);

  useEffect(() => {
    setSelected(isEnabled);
  }, [isEnabled]);

  // Style
  const style = {
    paddingRight  : '4px',
    paddingBottom : '10px',
    paddingLeft   : '5px',
    fontSize      : '8px'
  };
  // Label style
  const labelStyle = {
    paddingLeft : style.paddingLeft,
    fontSize    : style.fontSize
  };

  /**
   * @private
   */
  const legendItemStyles = {
    top : {
      display       : 'flex',
      paddingBottom : style.paddingBottom,
      paddingRight  : style.paddingRight
    },
    bottom : {
      display      : 'flex',
      paddingRight : style.paddingRight
    }
  };

  /**
   * @private
   */
  const styleOverrides = {
    root : {
      width         : '117px',
      verticalAlign : 'baseline',
      '& input'     : {
        height : '0px'
      }
    },
    disabled : {
      backgroundColor : 'rgba(255, 255, 255, 0.38)'
    },
    inputRoot : {
      '& input' : {
        paddingLeft : '11px'
      }
    },
    fieldLabel : {
      fontSize     : '11px',
      display      : 'inline-block',
      paddingRight : '10px'
    }
  };

  /**
   * @private
   */
  const legendlabelStyle = {
    fontWeight : 'bold',
    fontSize   : '10px'
  };

  /**
   * @private
   */
  const keyStyle = {
    fontSize : style.fontSize,
    position : 'absolute' as any,
    display  : 'inherit'
  };

  /**
   * @private
   */
  const legendByInputStyle = {
    label : {
      width       : '20%',
      float       : 'left' as any,
      paddingTop  : 10,
      paddingLeft : 2
    },
    image : {
      width : '78%',
      float : 'right' as any
    }
  };

  /**
   * @private
   */
  const chipContainerStyle = {
    width         : '265px',
    flexWrap      : 'wrap' as any,
    paddingBottom : 10,
    paddingTop    : 10
  };

  /**
   * @private
   */
  const fontStyle = `
    font-family: Arial, Helvetica, sans-serif;
    font-weight: 500;
    font-style: normal;
    font-size: 8px;
    text-transform: uppercase;
  `;

  /**
   * @private
   */
  const colorBar = {
    label      : translate('maps:history.inputEventColorBar.now').toUpperCase(),
    adornment  : translate('maps:history.inputEventColorBar.hrsAgo').toUpperCase(),
    width      : 200,
    height     : 20,
    labelColor : dlsColor('grey3'),
    fontStyle
  };

  /**
   * @private
   */
  const getBackgroundColor = (color: string) => {
    const backgroundColor = `${color}1A`;
    return {
      backgroundColor,
      hover : {
        '&:focus, &:hover' : {
          backgroundColor
        }
      }
    };
  };

  /**
   * @private
   */
  const isValid = useCallback(interval => interval >= MIN_HOURS && interval <= MAX_HOURS, []);

  /**
  * @private
  */
  const runReport = useCallback((interval, force: boolean = false) => {
    loadAssetHistory(interval, force);
  }, []);

  /**
   * @private
   */
  const setLayerVisible = useCallback(debounce(checked => {
    setSelected(checked);
    onToggle(HISTORY_LAYER, checked);
    if (checked) {
      runReport(interval);
    }
  }, 1000), [interval, layers]);

  /**
   * @private
   */
  const onToggleLayer = useCallback(({target: {checked}}) => {
    setLayerVisible(checked);
  }, [interval, layers]);

  /**
   * @private
   */
  const onRadioChanges = useCallback(({target}) => {
    setHistoryReportType(target.value);
  }, []);

  /**
   * @private
   */
  const getLabel = useCallback((label: string): string => {
    const {Mixed} = HistoryInputEventsEnum;
    return label === Mixed ? translate('maps:history.both') : label;
  }, []);

  /**
   * @private
   */
  const onInputChanges = useCallback(label => {
    const inputEvents = [];
    const {Mixed}     = HistoryInputEventsEnum;
    historyInputEvents?.forEach((input) => {
      const {label: name, isSelected, color} = input;
      if (name === label) {
        inputEvents.push({label: name, isSelected: !isSelected, color});
      } else {
        name !== Mixed && inputEvents.push(input);
      }
    });
    inputEvents.push(getInputMixedSelection(inputEvents, MAX_INPUT_SELECTED));
    setHistoryInputEvents(inputEvents);
  }, [historyInputEvents]);

  /**
   * @private
   */
  const handleNumberFieldChange = useCallback(debounce(({target: {valueAsNumber}}) => {
    if (!isNaN(valueAsNumber)) {
      if (isValid(valueAsNumber)) {
        setHistoryInterval(valueAsNumber);
        setInterval(valueAsNumber);
        if (isEnabled) {
          runReport(valueAsNumber, true);
        }
        setError(false);
        return;
      }
    }
    setError(true);
  }, 1000), [isEnabled]);

  /**
  * InputEvents
  *
  * Defines the Inputs Event Section for the History By Event
  */
  const InputEvents = ({inputEvents}: {inputEvents?: HistoryInputEvents[]}) => {
    const {Mixed, None}   = HistoryInputEventsEnum;
    const inputSelected   = inputEvents?.filter(({isSelected}) => isSelected);
    const isMixedSelected = inputSelected.length >= MAX_INPUT_SELECTED;
    const chips = inputEvents.filter(({label}) => label !== Mixed && label !== None);
    if (chips?.length) {
      return (
        <div className={styles.subLayersChip} style={chipContainerStyle}>
          {chips?.map(({label, isSelected, color}) => (
            <div key={label} style={{marginBottom: 5}}>
              <Chip label={label.toUpperCase()}
                variant='outlined'
                isSelected={isSelected}
                defaultColor={color}
                disabled={isMixedSelected ? !isSelected : false}
                backgroundStyles={getBackgroundColor(color)}
                onClick={function() { onInputChanges(label); }}
                onDelete={function() { onInputChanges(label); }} />
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  /**
  * LegendByEvent
  *
  * Defines the Legend Section for the History By Event
  */
  const LegendByEvent = ({inputEvents}: {inputEvents?: HistoryInputEvents[]}) => {
    const inputSelected = inputEvents.filter(({isSelected, label}) => isSelected &&
          label !== HistoryInputEventsEnum.None);
    if (inputSelected.length) {
      return (
        <>
          <div className={styles.subContainer}>
            <div style={{display: 'grid'}}>
              {inputSelected?.map(({label, color}, index) => (
                <div key={index}>
                  <div style={{paddingBottom: 30}}>
                    <div style={legendByInputStyle.label}>
                      <label className={classes.legendLabel}
                        style={{...legendlabelStyle, color}}>
                        {getLabel(label).toUpperCase()}
                      </label>
                    </div>
                    <div style={legendByInputStyle.image}>
                      <img src={getInputEventColorBar({...colorBar, value: interval, color})} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }
    return null;
  };

  /**
  * LegendByAsset
  *
  * Defines the Legend Section for the History By Asset
  */
  const LegendByAsset = () => {
    return (
      <>
        <label className={classes.legendLabel} style={{fontSize: style.fontSize}}>
          {translate('maps:history.legendByAsset.title')}
        </label>
        <div className={styles.subContainer} style={{paddingTop: '10px'}}>
          <div>
            <div style={legendItemStyles.top}>
              <IgnitionOnIcon size={LEGEND_ICON_SIZE} />
              <label className={classes.legendLabel} style={labelStyle}>
                {translate('maps:history.legendByAsset.igOn')}
              </label>
            </div>
            <div style={legendItemStyles.bottom}>
              <StopIcon size={LEGEND_ICON_SIZE} />
              <label className={classes.legendLabel} style={labelStyle}>
                {translate('maps:history.legendByAsset.stop')}
              </label>
            </div>
          </div>
          <div>
            <div style={legendItemStyles.top}>
              <IgnitionOffIcon size={LEGEND_ICON_SIZE} />
              <label className={classes.legendLabel} style={labelStyle}>
                {translate('maps:history.legendByAsset.igOff')}
              </label>
            </div>
            <div style={legendItemStyles.bottom}>
              <BatteryLowIcon size={LEGEND_ICON_SIZE} />
              <label className={classes.legendLabel} style={labelStyle}>
                {translate('maps:history.legendByAsset.batLow')}
              </label>
            </div>
          </div>
          <div>
            <div style={legendItemStyles.top}>
              <HarshEventIcon size={LEGEND_ICON_SIZE} />
              <label className={classes.legendLabel} style={labelStyle}>
                {translate('maps:history.legendByAsset.harshEvent')}
              </label>
            </div>
            <div style={legendItemStyles.bottom}>
              <InputEventIcon size={LEGEND_ICON_SIZE} />
              <label className={classes.legendLabel} style={labelStyle}>
                {translate('maps:history.legendByAsset.inputEvent')}
              </label>
            </div>
          </div>
        </div>
      </>
    );
  };

  /**
  * Legend
  *
  * Defines the Legend Section
  */
  const Legend = () => {
    if (isByAssetActive || inputEventsSelected.length > 1) {
      return (
        <>
          <label className={classes.legendLabel} style={keyStyle}>
            {translate('maps:history.legendByAsset.key')}
          </label>
          <div className={classes.assetContainer} style={{minWidth: 260, paddingLeft: isByAssetActive ? 10 : 2}}>
            {isByAssetActive ? <LegendByAsset /> : <LegendByEvent inputEvents={historyInputEvents} />}
          </div>
        </>
      );
    }
    return null;
  };

  /**
   * Details
   *
   * Defines History details layout
   */
  const Details = (
    <div>
      <FormTextField style={styleOverrides}
        type='number'
        maxLength={2}
        onChange={handleNumberFieldChange}
        fieldLabel={detailLabel.toUpperCase()}
        InputProps={{
          endAdornment : <InputAdornment position='end'>{translate('maps:history.hours')}</InputAdornment>
        }}
        hasError={error}
        errorText={translate('maps:history.error')}
        variant={'outlined'}
        defaultValue={historyIntervalInHours} />
      <RadioGroup id='radioGroup' row>
        <RadioButton onChange={onRadioChanges}
          checked={isByAssetActive}
          label={translate('maps:history.viewByAsset')}
          value={HistoryReportTypeEnum.Asset} />
        <RadioButton onChange={onRadioChanges}
          checked={!isByAssetActive}
          label={translate('maps:history.viewByInput')}
          value={HistoryReportTypeEnum.Event} />
      </RadioGroup>
      {!isByAssetActive && <InputEvents inputEvents={historyInputEvents} />}
      <Legend />
      {assetsHistoryLoading && <ButtonSpinner size={15} margin={'2px 0px 0px 0px'} />}
    </div>
  );

  return (
    <div id='history' className={classes.root}>
      <Grid container direction='row'>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion id={label}
              expandedLayer={expandedLayer}
              onToggleExpand={onToggleExpand}
              header={<LayerSettingsHeader label={label}
                isSelected={enabled}
                onToggle={onToggleLayer}/>} details={Details}
              detailsRootOverride={classes.accordionDetails} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};
export default TripHistoryLayerSettings;
